import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, Maximize2, X } from "lucide-react";
import { absUrl } from "@/lib/api";

const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${String(r).padStart(2, "0")}`;
};

export default function VideoDialog({ project, onClose }) {
    const videoRef = useRef(null);
    const trackRef = useRef(null);
    const shellRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [time, setTime] = useState("0:00");
    const [duration, setDuration] = useState("0:00");
    const [mediaError, setMediaError] = useState(false);
    const hasVideo = !!project.video_url && !mediaError;

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === " " && videoRef.current) {
                e.preventDefault();
                toggle();
            }
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, []);

    useEffect(() => {
        if (!hasVideo) return;
        const v = videoRef.current;
        if (!v) return;
        v.play()
            .then(() => setPlaying(true))
            .catch(() => {
                v.muted = true;
                setMuted(true);
                v.play()
                    .then(() => setPlaying(true))
                    .catch(() => setPlaying(false));
            });
    }, []);

    const toggle = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            v.play().catch(() => setMediaError(true));
            setPlaying(true);
        } else {
            v.pause();
            setPlaying(false);
        }
    };

    const seek = (e) => {
        const v = videoRef.current;
        const track = trackRef.current;
        if (!v || !v.duration || !track) return;
        const rect = track.getBoundingClientRect();
        v.currentTime = ((e.clientX - rect.left) / rect.width) * v.duration;
    };

    const toggleMute = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setMuted(v.muted);
    };

    const fullscreen = () => {
        if (document.fullscreenElement) document.exitFullscreen();
        else shellRef.current?.requestFullscreen?.();
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] bg-ink"
            data-testid="video-dialog"
        >
            <div ref={shellRef} className="absolute inset-0 flex flex-col">
                <div className="flex items-start justify-between px-5 md:px-10 py-5 text-paper">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-paper/60">
                            {project.client || "Independent"}{project.category ? ` — ${project.category}` : ""}
                        </p>
                        <h3 className="font-display font-extrabold uppercase text-lg md:text-2xl tracking-tight">
                            {project.title}
                        </h3>
                    </div>
                    <button
                        data-testid="video-dialog-close"
                        onClick={onClose}
                        aria-label="Close player"
                        className="border border-paper/30 p-3 hover:bg-paper hover:text-ink transition-colors duration-300"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="relative flex-1 flex items-center justify-center min-h-0 px-5 md:px-10">
                    {hasVideo ? (
                        <video
                            ref={videoRef}
                            data-testid="video-dialog-video"
                            src={absUrl(project.video_url)}
                            poster={absUrl(project.thumbnail_url)}
                            className="max-h-full max-w-full"
                            playsInline
                            onClick={toggle}
                            onError={() => setMediaError(true)}
                            onTimeUpdate={(e) => {
                                setProgress(e.target.currentTime / (e.target.duration || 1));
                                setTime(fmt(e.target.currentTime));
                            }}
                            onLoadedMetadata={(e) => setDuration(fmt(e.target.duration))}
                        />
                    ) : (
                        project.thumbnail_url && (
                            <img
                                src={absUrl(project.thumbnail_url)}
                                alt={project.title}
                                className="max-h-full max-w-full object-contain"
                            />
                        )
                    )}
                    {hasVideo && !playing && (
                        <button
                            onClick={toggle}
                            data-testid="video-dialog-bigplay"
                            aria-label="Play"
                            className="absolute inset-0 flex items-center justify-center"
                        >
                            <span className="border border-paper/50 text-paper p-8 hover:bg-paper hover:text-ink transition-colors duration-300">
                                <Play size={28} />
                            </span>
                        </button>
                    )}
                </div>

                {hasVideo && (
                    <div className="flex items-center gap-4 md:gap-6 px-5 md:px-10 py-5 text-paper text-[11px] uppercase tracking-[0.18em]">
                        <button
                            data-testid="video-dialog-toggle"
                            onClick={toggle}
                            aria-label={playing ? "Pause" : "Play"}
                            className="border border-paper/30 p-3 hover:bg-paper hover:text-ink transition-colors duration-300"
                        >
                            {playing ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <span className="tabular-nums whitespace-nowrap" data-testid="video-dialog-time">
                            {time} / {duration}
                        </span>
                        <div
                            ref={trackRef}
                            data-testid="video-dialog-progress"
                            onClick={seek}
                            className="relative flex-1 h-6 flex items-center cursor-pointer"
                        >
                            <div className="w-full h-px bg-paper/30 relative">
                                <div
                                    className="absolute left-0 top-0 h-full bg-paper transition-[width] duration-150"
                                    style={{ width: `${(progress || 0) * 100}%` }}
                                />
                            </div>
                        </div>
                        <button
                            data-testid="video-dialog-mute"
                            onClick={toggleMute}
                            aria-label={muted ? "Unmute" : "Mute"}
                            className="border border-paper/30 p-3 hover:bg-paper hover:text-ink transition-colors duration-300"
                        >
                            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                        </button>
                        <button
                            data-testid="video-dialog-fullscreen"
                            onClick={fullscreen}
                            aria-label="Fullscreen"
                            className="hidden md:block border border-paper/30 p-3 hover:bg-paper hover:text-ink transition-colors duration-300"
                        >
                            <Maximize2 size={14} />
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
